
import { 
  Box, 
  Stack, 
  Typography, 
  Button, 
  CircularProgress, 
  Chip,
  Paper,
  Fade,
  Tooltip
} from '@mui/material';
import { 
  Article, 
  Download, 
  Save, 
  CheckCircle,
  CloudUpload, 
  Pending,
  Warning,
  Info
} from '@mui/icons-material';
import Page from '../interfaces/page';
import { Editor } from '@tiptap/react';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import saveNotePDF from '../utils/save-note-pdf';
import { uploadResource } from '../../../../../utils/upload-resource';
import { useSnackbar } from '../../../../../components/snackbar';

interface ActionBarProps {
  title: string;
  classroom_id: string;
  resource_url: string;
  mode: number;
  pages: Page[],
  extensions: any[],
  saved: boolean;
  isSaving: boolean;
  onSave: () => Promise<void>;
}


function ActionBar({ 
  title, 
  classroom_id, 
  resource_url, 
  mode, 
  pages, 
  extensions, 
  saved, 
  isSaving,
  onSave
}: ActionBarProps) {
  const { showMessage } = useSnackbar();


  const handlePrint = async () => {
    await saveNotePDF(pages, title, extensions);
  };
  
  useEffect(() => {
    if(!saved) onSave();
  }, [saved]);

  return (
    <Paper
      elevation={mode === 1 ? 1 : 0}
      sx={{
        backgroundColor: 'transparent',
        borderRadius: 2,
        boxShadow: 'none', 
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2
        }}
      >
      <Stack 
        direction="row" 
        spacing={2}
        sx={{
          alignItems: 'center',
          flexGrow: 1,
          minWidth: 0 
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: 3,
            background: mode === 1 
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Article 
            sx={{ 
              fontSize: 28, 
              color: 'white',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
            }} 
          />
        </Paper>
        
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography
            variant="h6"
            sx={{
              color: mode === 1 ? "grey.800" : "#fff",
              fontWeight: 600,
              fontSize: '1.1rem',
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 300
            }}
          >
            {title}
          </Typography>
        </Box>

      <Fade in={true}>
        <Chip
          icon={
            isSaving ? (
              <CircularProgress size={16} sx={{ color: '#facc15' }} />
            ) : saved ? (
              <CheckCircle sx={{ fontSize: 16 }} />
            ) : (
              <Info sx={{ fontSize: 16 }} />
            )
          }
          label={
            isSaving
              ? "Saving..."
              : saved
                ? "All changes saved"
                : "Not saved (Ctrl+S)"
          }

          size="small"
          sx={{
            backgroundColor: isSaving
              ? 'rgba(250, 204, 21, 0.1)' // yellow-400
              : saved
                ? 'rgba(16, 185, 129, 0.1)' // green-500
                : 'rgba(239, 68, 68, 0.1)', // red-500
            color: isSaving
              ? '#facc15' // yellow-400
              : saved
                ? '#10b981' // green-500
                : '#ef4444', // red-500
            border: isSaving
              ? '1px solid rgba(250, 204, 21, 0.2)'
              : saved
                ? '1px solid rgba(16, 185, 129, 0.2)'
                : '1px solid rgba(239, 68, 68, 0.2)',
            '& .MuiChip-icon': {
              color: isSaving
                ? '#facc15'
                : saved
                  ? '#10b981'
                  : '#ef4444',
            },
          }}
        />
      </Fade>


      </Stack>

        <Stack 
          direction="row" 
          spacing={1.5}
          sx={{ alignItems: 'center' }}
        >
          <Tooltip title="Download as PDF" arrow>
            <Button
              variant="outlined"
              onClick={handlePrint}
              startIcon={<Download />}
              sx={{
                minWidth: 120,
                height: 42,
                borderRadius: 3,
                border: '2px solid',
                borderColor: mode === 1 ? '#f59e0b' : 'rgba(245, 158, 11, 0.5)',
                color: mode === 1 ? '#f59e0b' : '#fbbf24',
                backgroundColor: mode === 1 ? 'rgba(245, 158, 11, 0.05)' : 'rgba(245, 158, 11, 0.1)',
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'none',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  borderColor: '#f59e0b',
                  backgroundColor: mode === 1 ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.15)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(245, 158, 11, 0.25)'
                },
                '& .MuiButton-startIcon': {
                  marginRight: 1
                }
              }}
            >
              Download
            </Button>
          </Tooltip>

        </Stack>
      </Box>

     
    </Paper>
  );
}


export default memo(ActionBar);